import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../components/ui/dialog";
import { toast } from "sonner";
import { Plus, Edit3, Trash2, ShieldCheck, Mail, UserPlus, MessageCircle, Shuffle, Volume2, PenLine } from "lucide-react";

const VIBE_OPTIONS = [
  "romantic", "sensual", "sexy", "hot", "tense", "dangerous", "playful", "tender", "dark", "melancholy"
];

const ThemeForm = ({ initial, onSave }) => {
  const [form, setForm] = useState(initial || { name: "", description: "", default_vibes: [] });

  const toggleVibe = (v) => {
    setForm((f) => {
      const has = (f.default_vibes || []).includes(v);
      if (has) return { ...f, default_vibes: f.default_vibes.filter(x => x !== v) };
      if ((f.default_vibes || []).length >= 6) return f;
      return { ...f, default_vibes: [...(f.default_vibes || []), v] };
    });
  };

  return (
    <div className="space-y-5">
      <div>
        <Label className="font-sans-ui text-[10px] uppercase tracking-[0.3em] text-stone-500">Name</Label>
        <Input data-testid="theme-form-name" value={form.name} onChange={(e) => setForm({...form, name: e.target.value})}
          className="mt-2 bg-stone-900 border-stone-800 rounded-none h-12 text-stone-100 font-serif-reading" />
      </div>
      <div>
        <Label className="font-sans-ui text-[10px] uppercase tracking-[0.3em] text-stone-500">Description</Label>
        <Textarea data-testid="theme-form-desc" value={form.description} onChange={(e) => setForm({...form, description: e.target.value})}
          className="mt-2 bg-stone-900 border-stone-800 rounded-none text-stone-100 font-serif-reading min-h-[100px]" />
      </div>
      <div>
        <Label className="font-sans-ui text-[10px] uppercase tracking-[0.3em] text-stone-500">
          Default vibes <span className="text-stone-600 normal-case tracking-normal ml-1">(up to 4, pre-fill the wizard)</span>
        </Label>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-2 mt-3">
          {VIBE_OPTIONS.map((v) => {
            const sel = (form.default_vibes || []).includes(v);
            const disabled = !sel && (form.default_vibes || []).length >= 6;
            return (
              <button
                key={v}
                type="button"
                data-testid={`theme-vibe-${v}`}
                disabled={disabled}
                onClick={() => toggleVibe(v)}
                className={`p-3 border font-sans-ui text-xs uppercase tracking-[0.2em] transition ${
                  sel
                    ? "border-rose-800 bg-rose-950/30 text-rose-200"
                    : disabled
                      ? "border-stone-900 bg-stone-950 text-stone-600 opacity-50 cursor-not-allowed"
                      : "border-stone-800 bg-stone-900 text-stone-400 hover:text-stone-100"
                }`}
              >{v}</button>
            );
          })}
        </div>
      </div>
      <DialogFooter>
        <Button data-testid="theme-form-save" onClick={() => {
          if (!form.name || !form.description) { toast.error("Name and description required"); return; }
          onSave(form);
        }} className="bg-rose-900 hover:bg-rose-800 text-rose-50 rounded-none h-11 font-sans-ui text-xs uppercase tracking-[0.25em]">
          Save theme
        </Button>
      </DialogFooter>
    </div>
  );
};

const Admin = () => {
  const { user } = useAuth();
  const [themes, setThemes] = useState([]);
  const [chars, setChars] = useState([]);
  const [themeOpen, setThemeOpen] = useState(false);
  const [editingTheme, setEditingTheme] = useState(null);
  const [allowed, setAllowed] = useState([]);
  const [newEmail, setNewEmail] = useState("");
  const [addingEmail, setAddingEmail] = useState(false);
  const [users, setUsers] = useState([]);
  const [togglingUser, setTogglingUser] = useState(null);

  const load = async () => {
    try {
      const [t, c, a, u] = await Promise.all([
        api.get("/themes"),
        api.get("/characters"),
        api.get("/admin/allowed-emails"),
        api.get("/admin/users"),
      ]);
      setThemes(t.data);
      setChars(c.data.filter(x => x.is_predefined));
      setAllowed(a.data);
      setUsers(u.data);
    } catch { toast.error("Load failed"); }
  };

  useEffect(() => { load(); }, []);

  const addAllowedEmail = async (e) => {
    e?.preventDefault();
    const email = newEmail.trim().toLowerCase();
    if (!email || !email.includes("@")) { toast.error("Enter a valid email"); return; }
    setAddingEmail(true);
    try {
      await api.post("/admin/allowed-emails", { email });
      toast.success(`${email} can now register.`);
      setNewEmail("");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not add email");
    } finally { setAddingEmail(false); }
  };

  const removeAllowedEmail = async (email) => {
    if (!confirm(`Revoke access for ${email}?`)) return;
    try {
      await api.delete(`/admin/allowed-emails/${encodeURIComponent(email)}`);
      toast.success("Removed from invite list");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    }
  };

  const saveTheme = async (data) => {
    try {
      if (editingTheme) await api.put(`/themes/${editingTheme.id}`, data);
      else await api.post("/themes", data);
      toast.success("Theme saved");
      setThemeOpen(false); setEditingTheme(null);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const delTheme = async (t) => {
    if (!confirm(`Delete ${t.name}?`)) return;
    try { await api.delete(`/themes/${t.id}`); toast.success("Removed"); load(); }
    catch { toast.error("Failed"); }
  };

  const toggleChatAccess = async (u) => {
    setTogglingUser(u.id);
    try {
      await api.patch(`/admin/users/${u.id}/chat-mode`, { chat_mode_enabled: !u.chat_mode_enabled });
      toast.success(
        !u.chat_mode_enabled
          ? `${u.email} can now use Texting Mode`
          : `Texting Mode revoked for ${u.email}`
      );
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    } finally {
      setTogglingUser(null);
    }
  };

  const toggleMixedAccess = async (u) => {
    setTogglingUser(u.id);
    try {
      await api.patch(`/admin/users/${u.id}/mixed-mode`, { mixed_mode_enabled: !u.mixed_mode_enabled });
      toast.success(
        !u.mixed_mode_enabled
          ? `${u.email} can now mix prose & texts`
          : `Mixed mode revoked for ${u.email}`
      );
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    } finally {
      setTogglingUser(null);
    }
  };

  const toggleSceneAccess = async (u) => {
    setTogglingUser(u.id);
    try {
      await api.patch(`/admin/users/${u.id}/scene-mode`, { scene_mode_enabled: !u.scene_mode_enabled });
      toast.success(
        !u.scene_mode_enabled
          ? `${u.email} can now use scene mode`
          : `Scene mode revoked for ${u.email}`
      );
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    } finally {
      setTogglingUser(null);
    }
  };

  const toggleLipsyncAccess = async (u) => {
    setTogglingUser(u.id);
    try {
      await api.patch(`/admin/users/${u.id}/lipsync`, { lipsync_enabled: !u.lipsync_enabled });
      toast.success(
        !u.lipsync_enabled
          ? `${u.email} can now use voice playback`
          : `Voice playback revoked for ${u.email}`
      );
      load();
    } catch (err) {      toast.error(err.response?.data?.detail || "Failed");
    } finally {
      setTogglingUser(null);
    }
  };

  if (user?.role !== "admin") {
    return (
      <div className="min-h-screen pt-32 px-6 text-center">
        <p className="font-serif-reading italic text-stone-400">This room is for the house only.</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-24 pb-20 bg-stone-950 grain-overlay" data-testid="admin-page">
      <div className="max-w-6xl mx-auto px-6 lg:px-12">
        <div className="mb-16">
          <div className="flex items-center gap-3 mb-3">
            <ShieldCheck className="w-4 h-4 text-rose-800" strokeWidth={1.5} />
            <div className="overline text-xs font-sans-ui uppercase tracking-[0.3em] text-stone-500">
              House of Inkwell
            </div>
          </div>
          <h1 className="font-serif-heading text-5xl md:text-6xl font-light text-stone-100 leading-none">
            Admin Atelier
          </h1>
        </div>

        <Tabs defaultValue="themes">
          <TabsList className="bg-transparent border border-stone-800 rounded-none mb-10 h-12 p-0 inline-flex">
            <TabsTrigger data-testid="admin-tab-themes" value="themes" className="rounded-none font-sans-ui text-xs uppercase tracking-[0.2em] px-6 data-[state=active]:bg-stone-900 data-[state=active]:text-rose-200">
              Themes
            </TabsTrigger>
            <TabsTrigger data-testid="admin-tab-characters" value="chars" className="rounded-none font-sans-ui text-xs uppercase tracking-[0.2em] px-6 data-[state=active]:bg-stone-900 data-[state=active]:text-rose-200">
              Predefined Characters
            </TabsTrigger>
            <TabsTrigger data-testid="admin-tab-invites" value="invites" className="rounded-none font-sans-ui text-xs uppercase tracking-[0.2em] px-6 data-[state=active]:bg-stone-900 data-[state=active]:text-rose-200">
              Invites
            </TabsTrigger>
            <TabsTrigger data-testid="admin-tab-users" value="users" className="rounded-none font-sans-ui text-xs uppercase tracking-[0.2em] px-6 data-[state=active]:bg-stone-900 data-[state=active]:text-rose-200">
              Users
            </TabsTrigger>
          </TabsList>

          <TabsContent value="themes">
            <div className="flex justify-end mb-6">
              <Dialog open={themeOpen} onOpenChange={(o) => { setThemeOpen(o); if(!o) setEditingTheme(null); }}>
                <DialogTrigger asChild>
                  <Button data-testid="new-theme-btn" onClick={() => setEditingTheme(null)}
                    className="bg-rose-900 hover:bg-rose-800 text-rose-50 rounded-none h-11 px-5 font-sans-ui text-xs uppercase tracking-[0.25em]">
                    <Plus className="w-4 h-4 mr-2" strokeWidth={1.5} /> New theme
                  </Button>
                </DialogTrigger>
                <DialogContent className="bg-stone-950 border-stone-800 rounded-none max-w-xl max-h-[90vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle className="font-serif-heading text-3xl font-light text-stone-100">
                      {editingTheme ? "Refine" : "New"} theme
                    </DialogTitle>
                  </DialogHeader>
                  <ThemeForm initial={editingTheme} onSave={saveTheme} />
                </DialogContent>
              </Dialog>
            </div>
            <div className="grid md:grid-cols-2 gap-5">
              {themes.map((t) => (
                <div key={t.id} data-testid={`admin-theme-${t.id}`} className="border border-stone-800 bg-stone-900/30 p-6">
                  <div className="flex items-start justify-between mb-3">
                    <h3 className="font-serif-heading text-2xl text-stone-100">{t.name}</h3>
                    <div className="flex gap-2">
                      <button onClick={() => { setEditingTheme(t); setThemeOpen(true); }} data-testid={`admin-theme-edit-${t.id}`} className="text-stone-500 hover:text-stone-200"><Edit3 className="w-4 h-4" strokeWidth={1.5} /></button>
                      <button onClick={() => delTheme(t)} data-testid={`admin-theme-del-${t.id}`} className="text-stone-500 hover:text-rose-700"><Trash2 className="w-4 h-4" strokeWidth={1.5} /></button>
                    </div>
                  </div>
                  <p className="font-serif-reading text-stone-400 italic text-sm leading-relaxed mb-4">{t.description}</p>
                  <div className="flex flex-wrap gap-2">
                    {(t.default_vibes || []).map((v, i) => (
                      <span key={i} className="font-sans-ui text-[10px] uppercase tracking-[0.2em] text-rose-300 border border-rose-900/50 px-2 py-1">{v}</span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="chars">
            <p className="font-serif-reading italic text-stone-400 mb-6">
              Predefined characters are managed on the <a href="/characters" className="text-rose-300 hover:text-rose-200">Characters page</a> &mdash;
              toggle "Mark as predefined" while creating or editing to make them visible to all users.
            </p>
            <div className="grid md:grid-cols-3 gap-5">
              {chars.map((c) => (
                <div key={c.id} className="border border-stone-800 bg-stone-900/30 p-5">
                  <h4 className="font-serif-heading text-xl text-stone-100 mb-2">{c.name}</h4>
                  <p className="font-serif-reading text-stone-400 italic text-sm leading-relaxed">{c.description}</p>
                </div>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="invites">
            <div className="max-w-2xl">
              <p className="font-serif-reading italic text-stone-400 text-sm leading-relaxed mb-8">
                Only invited emails can register an account. Add an address to unlock registration;
                remove one to revoke future sign-ups (existing accounts are unaffected).
              </p>

              <form onSubmit={addAllowedEmail} className="flex items-center gap-3 mb-10" data-testid="invite-form">
                <Mail className="w-4 h-4 text-stone-600 shrink-0" strokeWidth={1.5} />
                <Input
                  type="email"
                  data-testid="invite-email-input"
                  placeholder="name@example.com"
                  value={newEmail}
                  onChange={(e) => setNewEmail(e.target.value)}
                  className="bg-stone-900 border-stone-800 rounded-none h-12 text-stone-100 font-serif-reading flex-1 focus:ring-rose-900"
                />
                <Button
                  type="submit"
                  disabled={addingEmail || !newEmail.trim()}
                  data-testid="invite-add-btn"
                  className="bg-rose-900 hover:bg-rose-800 text-rose-50 rounded-none h-12 px-5 font-sans-ui text-xs uppercase tracking-[0.25em]"
                >
                  <UserPlus className="w-4 h-4 mr-2" strokeWidth={1.5} />
                  {addingEmail ? "Adding…" : "Invite"}
                </Button>
              </form>

              <div className="overline text-[10px] font-sans-ui uppercase tracking-[0.3em] text-stone-500 mb-4">
                {allowed.length} {allowed.length === 1 ? "email" : "emails"} on the list
              </div>

              {allowed.length === 0 ? (
                <div className="border border-stone-800 p-8 text-center font-serif-reading italic text-stone-500">
                  No invites yet — registration is wide open until you add one.
                </div>
              ) : (
                <ul className="divide-y divide-stone-900 border-y border-stone-900">
                  {allowed.map((a) => (
                    <li
                      key={a.email}
                      data-testid={`invite-row-${a.email}`}
                      className="flex items-center justify-between gap-4 py-3.5"
                    >
                      <div className="min-w-0">
                        <div className="font-serif-reading text-stone-100 text-base truncate">{a.email}</div>
                        <div className="font-sans-ui text-[10px] uppercase tracking-[0.22em] text-stone-600 mt-0.5">
                          {a.added_by === "env_seed" ? "Seeded from env" : `Added by ${a.added_by}`}
                          {a.added_at && ` · ${new Date(a.added_at).toLocaleDateString()}`}
                        </div>
                      </div>
                      <button
                        onClick={() => removeAllowedEmail(a.email)}
                        data-testid={`invite-remove-${a.email}`}
                        className="text-stone-500 hover:text-rose-400 transition p-2 shrink-0"
                        title={`Revoke ${a.email}`}
                      >
                        <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </TabsContent>

          <TabsContent value="users">
            <div className="max-w-3xl">
              <p className="font-serif-reading italic text-stone-400 text-sm leading-relaxed mb-8">
                Grant <span className="text-rose-300">Texting Mode</span> to individual users. When enabled,
                they can create stories that read like a two-person SMS thread.
              </p>
              <div className="overline text-[10px] font-sans-ui uppercase tracking-[0.3em] text-stone-500 mb-4">
                {users.length} {users.length === 1 ? "user" : "users"}
              </div>
              {users.length === 0 ? (
                <div className="border border-stone-800 p-8 text-center font-serif-reading italic text-stone-500">
                  No users yet.
                </div>
              ) : (
                <ul className="divide-y divide-stone-900 border-y border-stone-900">
                  {users.map((u) => (
                    <li
                      key={u.id}
                      data-testid={`user-row-${u.email}`}
                      className="flex items-center justify-between gap-4 py-4"
                    >
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-3 flex-wrap">
                          <div className="font-serif-reading text-stone-100 text-base truncate">{u.display_name}</div>
                          <span className="font-sans-ui text-[10px] uppercase tracking-[0.2em] text-stone-500">{u.email}</span>
                          {u.role === "admin" && (
                            <span className="font-sans-ui text-[10px] uppercase tracking-[0.2em] text-rose-300 border border-rose-900/60 px-2 py-0.5">
                              admin
                            </span>
                          )}
                          {u.chat_mode_enabled && (
                            <span className="inline-flex items-center gap-1 font-sans-ui text-[10px] uppercase tracking-[0.2em] text-rose-200 bg-rose-950/40 border border-rose-900/60 px-2 py-0.5">
                              <MessageCircle className="w-3 h-3" strokeWidth={1.5} /> texting
                            </span>
                          )}
                          {u.mixed_mode_enabled && (
                            <span className="inline-flex items-center gap-1 font-sans-ui text-[10px] uppercase tracking-[0.2em] text-rose-200 bg-rose-950/40 border border-rose-900/60 px-2 py-0.5">
                              <Shuffle className="w-3 h-3" strokeWidth={1.5} /> mixed
                            </span>
                          )}
                          {u.scene_mode_enabled && (
                            <span className="inline-flex items-center gap-1 font-sans-ui text-[10px] uppercase tracking-[0.2em] text-rose-200 bg-rose-950/40 border border-rose-900/60 px-2 py-0.5">
                              <PenLine className="w-3 h-3" strokeWidth={1.5} /> scene
                            </span>
                          )}
                          {u.lipsync_enabled && (
                            <span className="inline-flex items-center gap-1 font-sans-ui text-[10px] uppercase tracking-[0.2em] text-rose-200 bg-rose-950/40 border border-rose-900/60 px-2 py-0.5">
                              <Volume2 className="w-3 h-3" strokeWidth={1.5} /> voice
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <button
                          onClick={() => toggleChatAccess(u)}
                          disabled={togglingUser === u.id || u.email === "admin@hush.app"}
                          data-testid={`user-toggle-chat-${u.email}`}
                          className={`font-sans-ui text-[10px] uppercase tracking-[0.25em] border px-3 py-1.5 transition ${
                            u.chat_mode_enabled
                              ? "border-rose-700 bg-rose-900/30 text-rose-200 hover:bg-rose-900/50"
                              : "border-stone-700 bg-stone-900 text-stone-400 hover:text-stone-100 hover:border-stone-500"
                          } disabled:opacity-40 disabled:cursor-not-allowed`}
                        >
                          {togglingUser === u.id
                            ? "…"
                            : u.chat_mode_enabled ? "Revoke texting" : "Grant texting"}
                        </button>
                        <button
                          onClick={() => toggleMixedAccess(u)}
                          disabled={togglingUser === u.id || u.email === "admin@hush.app"}
                          data-testid={`user-toggle-mixed-${u.email}`}
                          className={`font-sans-ui text-[10px] uppercase tracking-[0.25em] border px-3 py-1.5 transition ${
                            u.mixed_mode_enabled
                              ? "border-rose-700 bg-rose-900/30 text-rose-200 hover:bg-rose-900/50"
                              : "border-stone-700 bg-stone-900 text-stone-400 hover:text-stone-100 hover:border-stone-500"
                          } disabled:opacity-40 disabled:cursor-not-allowed`}
                        >
                          {togglingUser === u.id
                            ? "…"
                            : u.mixed_mode_enabled ? "Revoke mixed" : "Grant mixed"}
                        </button>
                        <button
                          onClick={() => toggleSceneAccess(u)}
                          disabled={togglingUser === u.id || u.email === "admin@hush.app"}
                          data-testid={`user-toggle-scene-${u.email}`}
                          className={`font-sans-ui text-[10px] uppercase tracking-[0.25em] border px-3 py-1.5 transition ${
                            u.scene_mode_enabled
                              ? "border-rose-700 bg-rose-900/30 text-rose-200 hover:bg-rose-900/50"
                              : "border-stone-700 bg-stone-900 text-stone-400 hover:text-stone-100 hover:border-stone-500"
                          } disabled:opacity-40 disabled:cursor-not-allowed`}
                        >
                          {togglingUser === u.id
                            ? "…"
                            : u.scene_mode_enabled ? "Revoke scene" : "Grant scene"}
                        </button>
                        <button
                          onClick={() => toggleLipsyncAccess(u)}
                          disabled={togglingUser === u.id || u.email === "admin@hush.app"}
                          data-testid={`user-toggle-voice-${u.email}`}
                          className={`font-sans-ui text-[10px] uppercase tracking-[0.25em] border px-3 py-1.5 transition ${
                            u.lipsync_enabled
                              ? "border-rose-700 bg-rose-900/30 text-rose-200 hover:bg-rose-900/50"
                              : "border-stone-700 bg-stone-900 text-stone-400 hover:text-stone-100 hover:border-stone-500"
                          } disabled:opacity-40 disabled:cursor-not-allowed`}
                        >
                          {togglingUser === u.id
                            ? "…"
                            : u.lipsync_enabled ? "Revoke voice" : "Grant voice"}
                        </button>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default Admin;
